import { Overlay, LAYER } from '@design/Overlay'
import { useEffect, useState } from 'react'
import { loadHistory, whenLabel, type HistoryEntry } from '@core/history'
import type { Payable } from '@core/payments'
import { Spinner } from '@design/components'

/**
 * What happened to this payment, in order, in sentences.
 *
 * The append-only ledger is only worth its awkwardness if someone can read it.
 * This is where "betaald gemarkeerd" and "teruggenomen" sit next to each
 * other, a year later, and explain themselves.
 *
 * Newest first, because the question is almost always "what happened last?"
 * rather than "how did this begin?".
 */
export function HistorySheet({
  payable,
  onClose,
}: {
  payable: Payable
  onClose: () => void
}) {
  const [entries, setEntries] = useState<HistoryEntry[] | null>(null)

  useEffect(() => {
    // Both, because the story is split across the two: the obligation carries
    // verification and deletion, the instalment carries the money.
    // Three, because the story is split: the obligation carries how it was
    // created and whether it was thrown away, the instalment carries the money
    // and the proof, and the document carries the reading and the correction.
    Promise.all([
      loadHistory('obligation', [payable.obligationId]),
      loadHistory('instalment', [payable.instalmentId]),
      payable.sourceDocumentId
        ? loadHistory('document', [payable.sourceDocumentId])
        : Promise.resolve([]),
    ])
      .then((lists) =>
        setEntries(lists.flat().sort((x, y) => y.at.localeCompare(x.at))),
      )
      .catch(() => setEntries([]))
  }, [payable.obligationId, payable.instalmentId, payable.sourceDocumentId])

  return (
    <Overlay layer={LAYER.overScreen}>
      <header className="shrink-0 pad-safe-top px-3 pb-2 flex items-center justify-between border-b border-line">
        <span className="font-medium px-2 truncate">
          {payable.supplierName ?? payable.description}
        </span>
        <button onClick={onClose} className="text-muted px-2 py-2 shrink-0">
          Klaar
        </button>
      </header>

      <div className="flex-1 scroll-area px-4 py-4 pad-safe-bottom">
        {!entries && <Spinner label="Even geduld…" />}

        {entries?.length === 0 && (
          <p className="text-muted text-sm text-center py-8">
            Er is nog niets gebeurd met deze betaling.
          </p>
        )}

        <ol className="relative">
          {(entries ?? []).map((entry, i) => (
            <li key={`${entry.at}-${i}`} className="flex gap-3 pb-4 last:pb-0">
              <div className="shrink-0 flex flex-col items-center">
                <span
                  className={`w-2.5 h-2.5 rounded-full mt-1.5 ${
                    entry.undone ? 'border-2 border-muted' : 'bg-ink'
                  }`}
                  aria-hidden
                />
                {i < (entries?.length ?? 0) - 1 && (
                  <span className="flex-1 w-px bg-line mt-1" aria-hidden />
                )}
              </div>
              <div className="min-w-0 pb-1">
                <p className={entry.undone ? 'text-muted' : ''}>{entry.line}</p>
                <p className="text-xs text-muted">{whenLabel(entry.at)}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </Overlay>
  )
}
